import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Button, Form, Select, InputNumber } from "antd";
import { ApolloError } from "@apollo/client";

import {
  Shop_Products_Insert_Input,
  Products,
  Maybe,
  Products_Set_Input,
} from "graphql/generated/graphql";
import { useGetAvailableProductsForShopQuery } from "graphql/queries/get-available-products-for-shop.generated";
import { useAddShopProductMutation } from "graphql/mutations/add-shop-product.generated";
import { useUpdateProductByIdMutation } from "graphql/mutations/update-product-by-id.generated";
import { useGetPropertiesByOrganizationIdQuery } from "graphql/queries/get-properties-by-organization-id.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import {
  useSelectedProperty,
  clearSelectedProperty,
} from "state/reactive-vars/selected-property-var";

import LoadingOverlay from "components/loading-overlay/loading-overlay";
import routeNames from "constants/route-names";

const { TextArea } = Input;

const ShopProductsAdd: React.FC = () => {
  const navigate = useNavigate();
  const { loggedUserValue } = useLoggedUser();
  const { selectedPropertyValue } = useSelectedProperty();
  const { selectedPropertyId, name: propertyName } = selectedPropertyValue;

  const [selectedPropertyKey, setSelectedPropertyKey] =
    useState<string>(selectedPropertyId);
  const [productOptions, setProductOptions] = useState<Array<Products>>([]);
  const [selectedProduct, setSelectedProduct] = useState<Maybe<Products>>(null);
  const [retailPrice, setRetailPrice] = useState<number | null>(null);
  const [extendedPrice, setExtendedPrice] = useState<number | null>(null);

  const handleOnCompleteGetPropertiesByOrganizationId = useCallback((data) => {
    // console.log("handleOnErrorOrganizations Complete data: ", data);
  }, []);

  const handleOnErrorGetPropertiesByOrganizationId = useCallback(
    (error: ApolloError) => {
      console.log("useGetPropertiesByOrganizationIdQuery Error: ", error);
    },
    []
  );

  const {
    data: propertiesByOrganizationData,
    loading: propertiesByOrganizationLoading,
  } = useGetPropertiesByOrganizationIdQuery({
    variables: {
      organization_id: loggedUserValue.organization_id,
    },
    errorPolicy: "none",
    fetchPolicy: "cache-first",
    nextFetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorGetPropertiesByOrganizationId,
    onCompleted: handleOnCompleteGetPropertiesByOrganizationId,
  });

  const handleOnSelectProperty = useCallback((value: string) => {
    setSelectedPropertyKey(value);
  }, []);

  const propertiesOptions = useMemo(() => {
    if (
      propertiesByOrganizationData &&
      propertiesByOrganizationData.properties &&
      propertiesByOrganizationData.properties.length
    ) {
      const propertyOptionsValues = propertiesByOrganizationData.properties.map(
        (element) => {
          return { value: element.id, label: element.name };
        }
      );

      return propertyOptionsValues;
    }

    return [];
  }, [propertiesByOrganizationData]);

  const handleOnCompleteGetProducts = useCallback(async (data) => {
    const { products } = data;

    const options = products.map((element) => {
      return { value: JSON.stringify(element), label: element.name };
    });

    setProductOptions(options);
  }, []);

  const handleOnErrorGetProducts = useCallback((error: ApolloError) => {
    console.log("handleOnErrorGetProducts Error: ", error);
  }, []);

  const { loading: loadingProducts } = useGetAvailableProductsForShopQuery({
    skip: !selectedPropertyKey || !loggedUserValue.organization_id,
    variables: {
      organization_id: loggedUserValue.organization_id,
      property_id: selectedPropertyKey,
    },
    errorPolicy: "none",
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: handleOnCompleteGetProducts,
    onError: handleOnErrorGetProducts,
  });

  const handleOnCompleteAddProductMutation = useCallback(
    (data: any) => {
      console.log("Complete Add Shop Product data: ", data);

      if (selectedPropertyKey) {
        const path = `${routeNames.ADMIN_PROPERTY_DETAILS.replace(
          ":propertyId",
          selectedPropertyKey
        )}`;
        clearSelectedProperty();

        navigate(path);
      }
    },
    [selectedPropertyKey]
  );

  const handleOnErrorAddProductMutation = useCallback((error: ApolloError) => {
    console.log("Error Add Shop Product Error: ", error);
  }, []);

  const [addShopProductMutation, { loading: loadingAddProducts }] =
    useAddShopProductMutation({
      errorPolicy: "none",
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onError: handleOnErrorAddProductMutation,
      onCompleted: handleOnCompleteAddProductMutation,
    });

  const handleOnCompleteUpdateProductMutation = useCallback((data: any) => {
    console.log("Complete update Product By Id Mutation data: ", data);
  }, []);

  const handleOnErrorUpdateProductMutation = useCallback(
    (error: ApolloError) => {
      console.log("updateProductByIdMutation Error: ", error);
    },
    []
  );

  const [updateProductByIdMutation, { loading }] = useUpdateProductByIdMutation(
    {
      errorPolicy: "none",
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onError: handleOnErrorUpdateProductMutation,
      onCompleted: handleOnCompleteUpdateProductMutation,
      refetchQueries: [
        "getProductById",
        "getPropertyById",
        "getAvailableProductsForShop",
      ],
    }
  );

  const uploadProduct = useCallback(async () => {
    if (selectedPropertyKey && selectedProduct) {
      try {
        if (retailPrice || extendedPrice) {
          let productInput: Products_Set_Input = {
            retail_price: retailPrice,
            extended_price: extendedPrice,
          };

          updateProductByIdMutation({
            variables: {
              set: productInput,
              id: selectedProduct.id,
            },
          });
        }
      } catch (error) {
        console.log("Error updating product price: ", error);
      }

      let installedProductInput: Shop_Products_Insert_Input = {
        product_id: selectedProduct.id,
        property_id: selectedPropertyKey,
      };

      addShopProductMutation({
        variables: {
          input: installedProductInput,
        },
      });
    }
  }, [
    selectedPropertyKey,
    selectedProduct,
    retailPrice,
    extendedPrice,
    loggedUserValue,
  ]);

  const isLoading =
    loadingProducts ||
    loadingAddProducts ||
    loading ||
    propertiesByOrganizationLoading;

  return (
    <div className="add-product-screen-container">
      <span className="add-product-page-title">{"Add Shop Device"}</span>

      <div className="add-product-form">
        <span className="add-product-form-title">{"Property"}</span>
        <Form.Item className="select-property-form-item">
          <Select
            value={selectedPropertyKey}
            defaultActiveFirstOption={false}
            className="add-product-attachment-input"
            popupClassName="property-popup"
            onChange={(value: string) => handleOnSelectProperty(value)}
            options={propertiesOptions}
            placeholder="Select Property"
            allowClear
            disabled={!!selectedPropertyId}
          />
        </Form.Item>
      </div>

      {selectedPropertyKey ? (
        <div className="add-product-form">
          <span className="add-product-form-title">{"Device"}</span>
          <Form.Item
            className="select-property-form-item"
            rules={[
              {
                required: true,
                message: "Please select a Device.",
              },
            ]}
            required
            validateTrigger="onBlur"
          >
            <Select
              value={
                selectedProduct ? JSON.stringify(selectedProduct) : undefined
              }
              defaultActiveFirstOption={false}
              className="add-product-attachment-input"
              onChange={(value) => {
                const objectValue = JSON.parse(value);

                console.log("selectedProduct objectValue: ", objectValue);

                if (objectValue.retail_price) {
                  setRetailPrice(objectValue.retail_price);
                }

                if (objectValue.extended_price) {
                  setExtendedPrice(objectValue.retail_price);
                }

                setSelectedProduct(objectValue);
              }}
              options={productOptions}
              placeholder={"Select Device"}
              allowClear
              disabled={isLoading}
            />
          </Form.Item>
        </div>
      ) : null}

      {selectedProduct ? (
        <Form className="add-product-form" autoComplete="off">
          <span className="add-product-form-title">{"Device Details"}</span>
          <Form.Item className="shop-product-add-form-item">
            <Input
              name="name"
              placeholder="Device Name"
              className="add-product-input-pair"
              value={selectedProduct.name}
              autoComplete="off"
              allowClear
              disabled={true}
            />
          </Form.Item>

          <div className="two-inputs-container">
            <Form.Item className="shop-product-add-form-item-pair">
              <Input
                name="make"
                placeholder="Make"
                className="add-product-input-pair"
                value={selectedProduct.make}
                autoComplete="off"
                allowClear
                disabled={true}
              />
            </Form.Item>

            <Form.Item className="shop-product-add-form-item-pair">
              <Input
                name="model"
                placeholder="Model"
                className="add-product-input-pair"
                value={selectedProduct.model}
                autoComplete="off"
                allowClear
                disabled={true}
              />
            </Form.Item>
          </div>

          <div className="two-inputs-container">
            <Form.Item className="shop-product-add-form-item-pair">
              <Input
                name="sku"
                placeholder="SKU"
                className="add-product-input-pair"
                value={selectedProduct.sku || ""}
                autoComplete="off"
                allowClear
                disabled={true}
              />
            </Form.Item>

            <Form.Item className="shop-product-add-form-item-pair">
              <Select
                value={selectedProduct.type}
                className="add-product-input-select"
                placeholder="Device Type"
                allowClear
                disabled={true}
              />
            </Form.Item>
          </div>

          <Form.Item className="shop-product-add-form-item">
            <TextArea
              name="description"
              placeholder="Description"
              style={{ height: "148px" }}
              value={selectedProduct.description}
              autoComplete="off"
              allowClear
              disabled={true}
            />
          </Form.Item>

          <Form.Item className="shop-product-add-form-item">
            <TextArea
              name="product-notes"
              placeholder="Device Notes"
              style={{ height: "137px" }}
              value={selectedProduct.product_notes || ""}
              autoComplete="off"
              allowClear
              disabled={true}
            />
          </Form.Item>

          <div className="two-inputs-container">
            <Form.Item className="shop-product-add-form-item-pair">
              <InputNumber
                name="retailPrice"
                placeholder="Retail Price"
                className="add-product-input-pair"
                value={retailPrice}
                disabled={isLoading}
                onChange={(value) => {
                  setRetailPrice(value);
                }}
              />
            </Form.Item>

            <Form.Item className="shop-product-add-form-item-pair">
              <InputNumber
                name="extendedPrice"
                placeholder="Extended Price"
                className="add-product-input-pair"
                value={extendedPrice}
                disabled={isLoading}
                onChange={(value) => {
                  setExtendedPrice(value);
                }}
              />
            </Form.Item>
          </div>
        </Form>
      ) : null}

      <div className="add-product-button-container">
        <Button
          className="admin-add-product-button"
          htmlType="submit"
          onClick={uploadProduct}
        >
          Save
        </Button>
      </div>

      <LoadingOverlay isLoading={isLoading} />
    </div>
  );
};

export default ShopProductsAdd;
